import React, { useEffect, useState } from 'react';
import { Box, Card, Heading, SimpleGrid, Spinner, Text, Select, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Flex, Badge, Button } from '@chakra-ui/react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowBackIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import Axios from '../../utils/httpClient';
import Layout from '../../sections/Layout';

const ProRectorDrillDown = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const initialKafedra = searchParams.get('kafedra') || '';
    const initialParameter = searchParams.get('parameter') || '';
    const initialDepartment = searchParams.get('department') || '';

    const [parameters, setParameters] = useState([]);
    const [partitions, setPartitions] = useState([]); // This stores unique kafedralar
    const [departments, setDepartments] = useState([]); // This stores bo'limlar
    const [selectedKafedra, setSelectedKafedra] = useState(initialKafedra);
    const [selectedParameter, setSelectedParameter] = useState(initialParameter);
    const [selectedDepartment, setSelectedDepartment] = useState(initialDepartment);
    const [drillDownUsers, setDrillDownUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingDrillDown, setLoadingDrillDown] = useState(false);

    useEffect(() => {
        fetchAllData();
    }, []);

    useEffect(() => {
        fetchDrillDownData();
        // Update URL params
        const params = {};
        if (selectedKafedra) params.kafedra = selectedKafedra;
        if (selectedParameter) params.parameter = selectedParameter;
        if (selectedDepartment) params.department = selectedDepartment;
        setSearchParams(params);
    }, [selectedKafedra, selectedParameter, selectedDepartment, partitions, departments]);

    const fetchAllData = async () => {
        try {
            setLoading(true);
            const [usersRes, paramsRes, deptRes] = await Promise.all([
                Axios().get('api/v1/account/user-results/'),
                Axios().get('api/v1/application/kpi-parameters/'),
                Axios().get('api/v1/account/partition/')
            ]);

            const users = usersRes?.data || [];
            // Use kafedra_id and kafedra_name for unique list
            const uniquePartitions = [...new Set(users.map(u => JSON.stringify({ id: u.kafedra_id, name: u.kafedra_name })))].map(p => JSON.parse(p)).filter(p => p.name);
            setPartitions(uniquePartitions);

            const depts = deptRes?.data || [];
            setDepartments(depts);

            const categories = paramsRes?.data?.categories || [];
            const allParams = categories.flatMap(cat => cat.parameters || []);

            // Remove duplicates by code
            const uniqueParams = [];
            const seenCodes = new Set();
            allParams.forEach(p => {
                if (!seenCodes.has(p.code)) {
                    seenCodes.add(p.code);
                    uniqueParams.push(p);
                }
            });
            setParameters(uniqueParams);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDrillDownData = async () => {
        try {
            setLoadingDrillDown(true);
            let url = 'api/v1/account/user-results/?';
            if (selectedKafedra) {
                const part = partitions.find(p => p.name === selectedKafedra);
                // Use kafedra_id for filtering if available
                if (part && part.id) url += `kafedra_id=${part.id}&`;
                else if (part) url += `kafedra_name=${encodeURIComponent(part.name)}&`;
            }
            if (selectedParameter) {
                url += `parameter_code=${selectedParameter}&`;
            }
            if (selectedDepartment) {
                url += `partition=${selectedDepartment}&`;
            }
            const res = await Axios().get(url);
            const usersWithPoints = (res?.data || []).filter(u => parseFloat(u.ball) > 0);
            setDrillDownUsers(usersWithPoints);
        } catch (error) {
            console.error("Error fetching drill down data:", error);
        } finally {
            setLoadingDrillDown(false);
        }
    };

    const exportToExcel = () => {
        if (!drillDownUsers || drillDownUsers.length === 0) return;

        const exportData = drillDownUsers.map((u, index) => ({
            "T/r": index + 1,
            "F.I.SH.": `${u.first_name} ${u.last_name}`,
            "Kafedra": u.kafedra_name,
            "To'plangan Ball": parseFloat(parseFloat(u.ball).toFixed(2))
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        
        // Auto-size columns
        const colWidths = [
            { wch: 5 }, // T/r
            { wch: 35 }, // F.I.SH.
            { wch: 45 }, // Kafedra
            { wch: 15 } // Ball
        ];
        worksheet['!cols'] = colWidths;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Hisobot");

        let filename = "KPI_Hisobot";
        if (selectedDepartment) {
            const d = departments.find(x => x.id.toString() === selectedDepartment.toString());
            if (d) filename += `_${d.name.substring(0, 20)}`;
        }
        if (selectedKafedra) filename += `_${selectedKafedra.substring(0, 20)}`;
        filename += ".xlsx";

        XLSX.writeFile(workbook, filename.replace(/[<>:"/\\|?*]/g, ''));
    };

    if (loading) {
        return (
            <Layout>
                <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                    <Spinner size="xl" color="blue.500" thickness="4px" />
                </Box>
            </Layout>
        );
    }

    return (
        <Layout>
            <Box p={5}>
                <Flex align="center" mb={5}>
                    <Button
                        leftIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/home')}
                        variant="solid"
                        bg="#003366"
                        color="white"
                        _hover={{ bg: "#002244" }}
                        mr={4}
                    >
                        Orqaga
                    </Button>
                    <Heading size="lg" color="#003366">KPI Batafsil Ma'lumotlar</Heading>
                </Flex>

                <Card p={6} boxShadow="xl" borderRadius="xl" border="1px solid" borderColor="gray.100" bg="white">
                    <Flex justify="space-between" align="center" mb={6}>
                        <Heading size="md" color="#003366">
                            Filtrlash va Saralash
                        </Heading>
                        <Flex gap={4} align="center">
                            {(selectedKafedra || selectedParameter || selectedDepartment) && (
                                <Badge
                                    colorScheme="red"
                                    variant="subtle"
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                    cursor="pointer"
                                    onClick={() => { setSelectedKafedra(''); setSelectedParameter(''); setSelectedDepartment(''); }}
                                    display="flex"
                                    alignItems="center"
                                >
                                    Filtrlarni tozalash <Text as="span" ml={2} fontWeight="bold">✕</Text>
                                </Badge>
                            )}
                            <Button
                                leftIcon={<DownloadIcon />}
                                colorScheme="green"
                                size="sm"
                                onClick={exportToExcel}
                                isDisabled={drillDownUsers.length === 0}
                            >
                                Excel
                            </Button>
                        </Flex>
                    </Flex>

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} mb={8} p={5} bg="gray.50" borderRadius="lg">
                        <Box>
                            <Text fontWeight="600" mb={2} color="gray.700" fontSize="xs" letterSpacing="wider">BO'LIM:</Text>
                            <Select
                                placeholder="Barcha bo'limlar"
                                value={selectedDepartment}
                                onChange={(e) => setSelectedDepartment(e.target.value)}
                                bg="white"
                                h="45px"
                                borderColor="gray.300"
                                _hover={{ borderColor: "blue.400" }}
                                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                            >
                                {departments.map(d => (
                                    <option key={d.id} value={d.id}>{d.name}</option>
                                ))}
                            </Select>
                        </Box>
                        <Box>
                            <Text fontWeight="600" mb={2} color="gray.700" fontSize="xs" letterSpacing="wider">KAFEDRA:</Text>
                            <Select
                                placeholder="Barcha kafedralar"
                                value={selectedKafedra}
                                onChange={(e) => setSelectedKafedra(e.target.value)}
                                bg="white"
                                h="45px"
                                borderColor="gray.300"
                                _hover={{ borderColor: "blue.400" }}
                                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                            >
                                {partitions.map((p, idx) => (
                                    <option key={p.id || idx} value={p.name}>{p.name}</option>
                                ))}
                            </Select>
                        </Box>
                        <Box>
                            <Text fontWeight="600" mb={2} color="gray.700" fontSize="xs" letterSpacing="wider">KPI PARAMETRI:</Text>
                            <Select
                                placeholder="Barcha parametrlar"
                                value={selectedParameter}
                                onChange={(e) => setSelectedParameter(e.target.value)}
                                bg="white"
                                h="45px"
                                borderColor="gray.300"
                                _hover={{ borderColor: "blue.400" }}
                                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                            >
                                {parameters.map(p => (
                                    <option key={p.id} value={p.code}>{p.code} - {p.title.substring(0, 60)}...</option>
                                ))}
                            </Select>
                        </Box>
                    </SimpleGrid>

                    {loadingDrillDown ? (
                        <Flex justify="center" p={10}>
                            <Spinner size="lg" color="blue.500" thickness="4px" />
                        </Flex>
                    ) : (
                        <Box>
                            <TableContainer borderRadius="lg" border="1px solid" borderColor="gray.200">
                                <Table variant="simple" size="md">
                                    <Thead bg="gray.50">
                                        <Tr>
                                            <Th color="gray.600">#</Th>
                                            <Th color="gray.600">F.I.SH.</Th>
                                            <Th color="gray.600">Kafedra</Th>
                                            <Th isNumeric color="gray.600">To'plangan Ball</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {drillDownUsers.length > 0 ? (
                                            drillDownUsers.map((u, idx) => (
                                                <Tr
                                                    key={u.id}
                                                    _hover={{ bg: "blue.50" }}
                                                    transition="0.2s"
                                                    cursor="pointer"
                                                    onClick={() => {
                                                        const params = new URLSearchParams();
                                                        if (selectedParameter) params.append('parameter_code', selectedParameter);
                                                        params.append('kafedra', u.kafedra_name);
                                                        navigate(`/dashboard/teacher-applications/${u.id}?${params.toString()}`);
                                                    }}
                                                >
                                                    <Td color="gray.500" fontSize="sm">{idx + 1}</Td>
                                                    <Td fontWeight="600" color="gray.800">
                                                        {u.first_name} {u.last_name}
                                                    </Td>
                                                    <Td fontSize="sm" color="gray.600">{u.kafedra_name}</Td>
                                                    <Td isNumeric fontWeight="bold" color="blue.600">
                                                        {parseFloat(u.ball).toFixed(2)}
                                                    </Td>
                                                </Tr>
                                            ))
                                        ) : (
                                            <Tr>
                                                <Td colSpan={4} textAlign="center" p={12}>
                                                    <Text color="gray.400" fontStyle="italic">
                                                        Tanlangan filtrlar bo'yicha ball to'plagan o'qituvchilar topilmadi.
                                                    </Text>
                                                </Td>
                                            </Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Card>
            </Box>
        </Layout>
    );
};

export default ProRectorDrillDown;
